import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import styles from "./styles.module.css";

// SCRUM-992: Default Account / Location / Landing Page dropdowns. State and
// saving live in useDefaultPreferences; the host form passes its own row and
// field classes so the dropdowns line up with its existing inputs, and calls
// defaults.save() from its own Update button.
const NONE_LABEL = "-- None (standard behavior) --";
const STALE_WARNING =
  "No longer available to this user. Login will use the standard behavior.";

const DefaultSelect = ({
  label,
  value,
  options,
  getId,
  getLabel,
  onChange,
  disabled,
  placeholder,
  error,
  warning,
  fieldClassName,
}) => {
  const selected = options.find((option) => String(getId(option)) === value);
  let display = NONE_LABEL;
  if (!value && placeholder) display = placeholder;
  else if (selected) display = getLabel(selected);
  else if (value) display = "Unavailable";

  return (
    <div className={`${fieldClassName} ${styles.field}`}>
      <label>{label}</label>
      <Select value={value} onChange={onChange} disabled={disabled}>
        <SelectTrigger className={error ? styles.triggerError : ""}>
          <SelectValue>
            <p>{display}</p>
          </SelectValue>
        </SelectTrigger>
        <SelectContent searchField={options.length > 8}>
          <SelectItem value="">{NONE_LABEL}</SelectItem>
          {options.map((option) => (
            <SelectItem key={getId(option)} value={String(getId(option))}>
              {getLabel(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && <span className={styles.error}>{error}</span>}
      {!error && warning && <span className={styles.warning}>{warning}</span>}
    </div>
  );
};

const DefaultPreferencesFields = ({
  defaults,
  rowClassName = "",
  fieldClassName = "",
}) => {
  if (!defaults?.isAvailable) return null;

  const {
    prefs,
    form,
    errors,
    accountOptions,
    locationOptions,
    locationsLoading,
    landingPageOptions,
    setAccount,
    setLocation,
    setLandingPage,
    loadFailed,
  } = defaults;

  // Stale warnings only apply while the stored value is still selected.
  const storedAccount = prefs?.default_account_id ?? "";
  const storedLocation =
    prefs?.default_location_id != null ? String(prefs.default_location_id) : "";
  const storedLandingPage = prefs?.default_landing_page ?? "";
  const accountStale =
    prefs?.default_account_valid === false &&
    !!storedAccount &&
    form.default_account_id === storedAccount;
  const locationStale =
    prefs?.default_location_valid === false &&
    !!storedLocation &&
    form.default_location_id === storedLocation;
  const landingPageStale =
    prefs?.default_landing_page_valid === false &&
    !!storedLandingPage &&
    form.default_landing_page === storedLandingPage;

  const noAccount = !form.default_account_id;
  const toErrorText = (value) => (Array.isArray(value) ? value.join(" ") : value);

  return (
    <>
      <div className={rowClassName}>
        <DefaultSelect
          label="Default Account"
          value={form.default_account_id}
          options={accountOptions}
          getId={(option) => option.id}
          getLabel={(option) => option.name}
          onChange={setAccount}
          error={toErrorText(errors.default_account_id)}
          warning={accountStale ? STALE_WARNING : null}
          fieldClassName={fieldClassName}
        />
        <DefaultSelect
          label="Default Location"
          value={form.default_location_id}
          options={locationOptions}
          getId={(option) => option.id}
          getLabel={(option) => option.name}
          onChange={setLocation}
          disabled={noAccount || locationsLoading}
          placeholder={locationsLoading ? "Loading locations..." : undefined}
          error={toErrorText(errors.default_location_id)}
          warning={locationStale ? STALE_WARNING : null}
          fieldClassName={fieldClassName}
        />
      </div>

      {/* Disabled until an account is chosen (AC 5). The options are
          role-based, so they're the same for every account. */}
      <div className={rowClassName}>
        <DefaultSelect
          label="Default Landing Page"
          value={form.default_landing_page}
          options={landingPageOptions}
          getId={(option) => option.id}
          getLabel={(option) => option.label}
          onChange={setLandingPage}
          disabled={noAccount}
          error={toErrorText(errors.default_landing_page)}
          warning={landingPageStale ? STALE_WARNING : null}
          fieldClassName={fieldClassName}
        />
      </div>

      {loadFailed && (
        <span className={styles.warning}>
          Couldn't load the saved defaults or their options. Please try again
          later.
        </span>
      )}
    </>
  );
};

export default DefaultPreferencesFields;
